import { useState, useEffect, useCallback, useRef } from 'react';
import { SecurityAlert } from '../types/security';
import { securityService } from '../services/securityService';

interface AlertNotification extends SecurityAlert {
  guest_name?: string;
  guest_photo?: string;
  hotel_name?: string;
  risk_factors?: string[];
}

const MAX_RECONNECT_ATTEMPTS = 5;
const BASE_RECONNECT_MS = 3000;

export const useSecurityAlerts = (wsUrl?: string) => {
  const [activeAlerts, setActiveAlerts] = useState<AlertNotification[]>([]);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attemptsRef = useRef(0);
  const closedByUsRef = useRef(false);

  const loadActiveAlerts = useCallback(async () => {
    try {
      const alerts = await securityService.getAlerts({ status: 'active', severity: 'critical' });
      setActiveAlerts(alerts as AlertNotification[]);
    } catch (error) {
      console.error('Error loading active alerts:', error);
    }
  }, []);

  const clearReconnectTimer = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  };

  const connectWebSocket = useCallback((url: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      // Tear down previous socket before opening a new one
      if (wsRef.current) {
        closedByUsRef.current = true;
        try {
          wsRef.current.close();
        } catch {
          /* ignore */
        }
        wsRef.current = null;
      }

      closedByUsRef.current = false;
      const wsConnection = new WebSocket(`${url}?token=${encodeURIComponent(token)}`);

      wsConnection.onopen = () => {
        attemptsRef.current = 0;
        console.log('WebSocket connected for security alerts');
      };

      wsConnection.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'security.alert.critical' || data.type === 'security.alert') {
            const alert: AlertNotification = data.data;

            if (alert.severity === 'critical' || alert.severity === 'high') {
              setActiveAlerts((prev) => [...prev, alert]);
            }
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      wsConnection.onerror = () => {
        // Browser fires error then close; avoid double noise — close handler schedules reconnect.
      };

      wsConnection.onclose = (ev) => {
        wsRef.current = null;
        if (closedByUsRef.current) {
          return;
        }
        // 1000 = normal close; still allow limited reconnect for network blips
        if (attemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          console.warn(
            `Security alerts WebSocket stopped after ${MAX_RECONNECT_ATTEMPTS} failed attempts (code ${ev.code}). REST polling still loads alerts.`
          );
          return;
        }
        const delay = BASE_RECONNECT_MS * Math.pow(2, attemptsRef.current);
        attemptsRef.current += 1;
        clearReconnectTimer();
        reconnectTimerRef.current = setTimeout(() => connectWebSocket(url), delay);
      };

      wsRef.current = wsConnection;
    } catch (error) {
      console.error('Error connecting WebSocket:', error);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    loadActiveAlerts();
    attemptsRef.current = 0;

    if (wsUrl) {
      connectWebSocket(wsUrl);
    }

    return () => {
      closedByUsRef.current = true;
      clearReconnectTimer();
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch {
          /* ignore */
        }
        wsRef.current = null;
      }
    };
  }, [wsUrl, loadActiveAlerts, connectWebSocket]);

  const acknowledgeAlert = useCallback((alertId: string) => {
    setActiveAlerts((prev) => prev.filter((alert) => alert.id !== alertId));
  }, []);

  const dismissAlert = useCallback((alertId: string) => {
    setActiveAlerts((prev) => prev.filter((alert) => alert.id !== alertId));
  }, []);

  return {
    activeAlerts,
    acknowledgeAlert,
    dismissAlert,
    refreshAlerts: loadActiveAlerts,
  };
};
